// Third-party embeds behind marketing consent (modules/consent).
//
// Placeholders ([data-son-embed-placeholder]) stand in for blocked embeds while
// marketing cookies are declined. Their Load button grants marketing consent
// through Cookiebot. Its submitCustomConsent() only records the consent
// (checked with CMP v5: no event, nothing unblocked, no reload), so the
// button also calls Cookiebot.runScripts(), which runs the blocked scripts
// (Klaviyo) and loads the blocked iframes in place; Google's consent signal
// follows on the next page load. The state is read from Cookiebot.consent
// right after the call, and polled for while the page loads. Blocked iframes
// (data-cookieblock-src) that Cookiebot has not loaded get their src here
// when their categories are consented, and Elementor's YouTube widget
// ([data-son-youtube]), which has no iframe to unblock, gets a plain embed
// built from its settings. Without Cookiebot on the page (blocked by an ad
// blocker, say) the button loads the one embed directly - the visitor asked
// for it.

const consent = () => (window.Cookiebot && window.Cookiebot.consent) || null;

const marketingAllowed = () => Boolean(consent() && consent().marketing);

// data-cookieconsent="statistics,marketing": every listed category must be in
function categoriesAllowed(list) {
    const current = consent();

    return Boolean(current) && list.split(',').map((name) => name.trim()).filter(Boolean).every((name) => current[name]);
}

function youtubeSrc(video, autoplay) {
    const host = video.privacy ? 'https://www.youtube-nocookie.com' : 'https://www.youtube.com';
    const params = new URLSearchParams({
        autoplay: autoplay || video.autoplay ? '1' : '0',
        mute: video.mute ? '1' : '0',
        controls: video.controls ? '1' : '0',
        rel: video.rel ? '1' : '0',
        cc_load_policy: video.cc ? '1' : '0',
        playsinline: '1',
    });
    if (video.loop) {
        params.set('loop', '1');
        params.set('playlist', video.id);
    }
    if (video.start) {
        params.set('start', String(video.start));
    }
    if (video.end) {
        params.set('end', String(video.end));
    }

    return `${host}/embed/${encodeURIComponent(video.id)}?${params}`;
}

function buildYouTube(host, autoplay) {
    if (host.dataset.sonYoutubeLoaded) {
        return;
    }
    host.dataset.sonYoutubeLoaded = 'true';

    let video;
    try {
        video = JSON.parse(host.dataset.sonYoutube);
    } catch (error) {
        return;
    }

    const iframe = document.createElement('iframe');
    iframe.className = 'son-embed-host__iframe';
    iframe.src = youtubeSrc(video, autoplay);
    iframe.title = 'YouTube video';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    host.appendChild(iframe);
}

// the placeholder the visitor clicked: its embed starts playing once consent is in
let requested = null;

function update() {
    const allowed = marketingAllowed();

    document.querySelectorAll('[data-son-embed-placeholder]').forEach((placeholder) => {
        placeholder.hidden = allowed;
    });

    document.querySelectorAll('iframe[data-cookieblock-src]:not([src])').forEach((iframe) => {
        if (categoriesAllowed(iframe.dataset.cookieconsent || 'marketing')) {
            iframe.src = iframe.dataset.cookieblockSrc;
            // Cookiebot's state stylesheet hides .cookieconsent-optin-* until it unblocks itself
            iframe.classList.remove('cookieconsent-optin', 'cookieconsent-optin-preferences', 'cookieconsent-optin-statistics', 'cookieconsent-optin-marketing');
            iframe.removeAttribute('data-cybot-cookiebot-blocked');
        }
    });

    if (allowed) {
        document.querySelectorAll('[data-son-youtube]').forEach((host) => {
            buildYouTube(host, requested !== null && host.contains(requested));
        });
        requested = null;
    }
}

// Cookiebot loads async (and may be blocked): read its state as soon as it is there
function waitForConsent() {
    let tries = 0;
    const tick = () => {
        update();
        if (!consent() && tries++ < 40) {
            setTimeout(tick, 250);
        }
    };
    tick();
}

// one embed, without Cookiebot: the iframe next to the placeholder, or the widget
function loadDirectly(placeholder) {
    placeholder.hidden = true;

    const host = placeholder.closest('[data-son-youtube]');
    if (host) {
        buildYouTube(host, true);
        return;
    }

    const iframe = placeholder.nextElementSibling;
    if (iframe && iframe.matches('iframe[data-cookieblock-src]')) {
        iframe.src = iframe.dataset.cookieblockSrc;
    }
}

document.addEventListener('click', (event) => {
    const load = event.target.closest('[data-son-embed-load]');
    if (load) {
        const placeholder = load.closest('[data-son-embed-placeholder]');
        if (window.Cookiebot && typeof window.Cookiebot.submitCustomConsent === 'function') {
            requested = placeholder;
            const current = consent() || {};
            window.Cookiebot.submitCustomConsent(Boolean(current.preferences), Boolean(current.statistics), true);
            if (typeof window.Cookiebot.runScripts === 'function') {
                window.Cookiebot.runScripts();
            }
            update();
        } else {
            loadDirectly(placeholder);
        }
        return;
    }

    if (event.target.closest('[data-son-embed-settings]') && window.Cookiebot && typeof window.Cookiebot.renew === 'function') {
        window.Cookiebot.renew();
    }
});

// the dialog's own buttons do fire these; the rest is covered by the poll above
['CookiebotOnLoad', 'CookiebotOnConsentReady', 'CookiebotOnAccept', 'CookiebotOnDecline'].forEach((name) => {
    window.addEventListener(name, update);
});
document.addEventListener('DOMContentLoaded', waitForConsent);
